import React, { useEffect, useState, useRef, useContext } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import Faqs from '../faqs/faqs';
import { Environment } from 'src/Environment';

import { Card, DonutChartComponent } from 'src/components';
import HeaderNav from '../../header-nav/header-nav';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import TealiumEvents from 'src/helpers/tealium-events';
import { IBCADashboard, getBCADashboardData } from 'src/services/bca-dashboard.service';
import {
  businessCashAdvanceTopUpCalculationRoute,
  paymentsDashboardRoute,
} from 'src/routing/routes';
import { BCACard } from 'src/containers/self-service/business-cash-advance/dashboard/components';

import './dashboard.scss';
import {
  BCALinkID,
  BCAPageUrl,
  BCAPageName,
  BCATopUpTealiumService,
} from 'src/containers/self-service/business-cash-advance/tagging/bca-top-up-tealium-service';
import { OfferContext } from 'src/context/offer-context';
import toastMessageServiceSingleton from 'src/services/toast-message.service';

const faqs = [
  {
    title: 'How long before my BCA is paid off?',
    text: "The advance will be paid off once your Business Cash Advance account balance is at zero. There's no set repayment term, the more revenue generated from your QR, POS or eCommerce device or platform, the quicker your cash advance will be settled. In other words, the more you transact, the quicker you pay it off.",
  },
  {
    title: 'What is my repayment trajectory?',
    text: 'This is an estimation of when your Business Cash Advance will be paid up. This is based on your average repayments to date. ',
  },
  {
    title: 'How do my repayments work?',
    text: 'The daily repayment percentage that you agree will be collected from your daily POS revenue. We deduct this from your daily settlement amount and allocate it to reduce your advance balance.',
  },
];

type CashAdvanceData = {
  name: string;
  value: number;
};

function BCADashboard() {
  const { merchant } = useMerchantProfile();
  const history = useHistory();
  const { offers, isBCATopUp } = useContext(OfferContext);
  const [totalCashAdvance, setTotalCashAdvance] = useState(formatCurrencyAmountSimple(0));
  const [recentRepayments, setRecentRepayments] = useState<IBCADashboard['accountTransactions']>(
    [],
  );
  const [cashAdvanceData, setCashAdvanceData] = useState<CashAdvanceData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const topOfPage = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const abortController = new AbortController();
    TealiumEvents.dashboardBCAPageLoad();

    const getDashboardData = async () => {
      setIsLoading(true);
      const response = await getBCADashboardData(abortController);
      const data = response.result;

      setIsLoading(false);
      if (!abortController.signal.aborted && data) {
        setRecentRepayments(data.accountTransactions);

        if (data && data.accountSummary?.[0]) {
          data.accountSummary[0].transactionDebitAmount =
            Number(data.accountSummary[0].transactionDebitAmount) || 0;
          data.accountSummary[0].transactionCreditAmount =
            Number(data.accountSummary[0].transactionCreditAmount) || 0;

          setTotalCashAdvance(data.accountSummary[0].transactionDebitAmount);

          setCashAdvanceData([
            {
              name: 'Total outstanding',
              value:
                data.accountSummary[0].transactionDebitAmount -
                data.accountSummary[0].transactionCreditAmount,
            },
            {
              name: 'Total repaid',
              value: data.accountSummary[0].transactionCreditAmount,
            },
          ]);
        }
      }
    };
    getDashboardData();
  }, []);

  useEffect(() => {
    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }
    isBCATopUp && toastMessageServiceSingleton.removeAllToasts();
  }, []);

  // TODO: BCA Flag to be removed
  if (Environment.current?.disabledFeatures.bca) {
    return <Redirect push to={paymentsDashboardRoute} />;
  }

  const donutChartPresent = !isLoading && cashAdvanceData?.length > 0;

  return (
    <div className="BCADashboard" data-testid="BCADashboard">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
        screenName="My documents"
      >
        <h1
          className="page-title has-text-left is-hidden-mobile has-margin-bottom-4"
          ref={topOfPage}
        >
          Business Cash Advance
        </h1>
        <div className="columns cards-row">
          <Card
            stretchVertical={true}
            className={`column donut-chart-card ${
              donutChartPresent ? 'has-donut-chart-child' : ''
            }`}
            cardTitle="Total cash advance"
          >
            {!isLoading || cashAdvanceData?.length > 0 ? (
              cashAdvanceData?.length > 0 ? (
                <div automationid={`BCA_Div_TotalCashAdvancePieChart`}>
                  <DonutChartComponent
                    formatter={(value) => formatCurrencyAmountSimple(value)}
                    className="pie-graph is-hidden-mobile"
                    valuePrefix="R "
                    maxRadius={120}
                    chartData={cashAdvanceData}
                    chartLabel={`R ${formatCurrencyAmountSimple(totalCashAdvance)}`}
                    chartSubLabel="Total sales"
                  ></DonutChartComponent>
                  <DonutChartComponent
                    formatter={(value) => formatCurrencyAmountSimple(value)}
                    className="pie-graph is-hidden-tablet"
                    valuePrefix="R "
                    maxRadius={100}
                    chartData={cashAdvanceData}
                    chartLabel={`R ${formatCurrencyAmountSimple(totalCashAdvance)}`}
                    chartSubLabel="Total sales"
                    isMobile={true}
                  ></DonutChartComponent>
                </div>
              ) : (
                <div
                  automationid={`BCA_Div_TotalCashAdvanceNoData`}
                  className="has-margin-5 is-size-5 has-text-centered"
                >
                  No data to display
                </div>
              )
            ) : (
              <div className="load-more has-margin-5 is-size-5 has-text-centered">Loading. . .</div>
            )}
          </Card>
          <Card className="column recent-repayments-card" cardTitle="Recent Repayments">
            {!isLoading ? (
              recentRepayments && recentRepayments.length > 0 ? (
                <table className="table is-fullwidth">
                  <tbody>
                    {recentRepayments.map((repayment, index) => (
                      <tr key={`recent-repayment-${index}`}>
                        <td>
                          <p className="table-data-description">R{repayment.transactionAmount}</p>
                          <p className="has-text-weight-light is-size-7 subtext">
                            {repayment.transactionDescription}
                          </p>
                        </td>
                        <td
                          automationid={`PaymentDashboard_TD_SettlementAmount`}
                          className="text-right table-data-price"
                        >
                          {DateTime.fromISO(repayment.processingDate).toFormat('dd/MM/yyyy')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div
                  className="has-margin-bottom-5 is-size-5 has-text-centered"
                  automationid="BCA_Div_RecentRepaymentsNoData"
                >
                  No data to display
                </div>
              )
            ) : (
              <div className="load-more has-margin-5 is-size-5 has-text-centered">Loading. . .</div>
            )}
          </Card>
        </div>

        {!Environment.current.disabledFeatures.bcaTopUp && isBCATopUp && offers && (
          <div className="settlement">
            <BCACard
              title="You qualify for a Top-up"
              description={`You have been pre-approved for a R${formatCurrencyAmountSimple(
                offers.offerDetails.AvailableLimit,
              )} Top-up of your Business Cash Advance`}
              buttonTitle="View offer"
              onButtonPress={() => {
                BCATopUpTealiumService.onUIInteraction(
                  BCAPageName.GET_FINANCED,
                  BCALinkID.GET_FINANCED,
                  BCAPageUrl.GET_FINANCED,
                );
                history.push(businessCashAdvanceTopUpCalculationRoute);
              }}
            />
          </div>
        )}

        <Faqs
          faqs={faqs}
          automationIdPrefix="Business_Cash_Advance_Faqs_Dashboard"
          faqOnClick={(topic) => TealiumEvents.dashboardBCAClickFAQ(topic)}
        />
      </HeaderNav>
    </div>
  );
}

export default BCADashboard;
